import React, { useEffect, useState } from 'react';
import { Button, Drawer, Space, Tree, message, Tooltip, Popconfirm, Upload, Spin } from 'antd';
import { FolderOpenOutlined, CloudUploadOutlined, CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { postReports } from '../../services/create_tree/get_tree';
import { faEye, faDownload, faTrash, faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteFile } from '../../services/create_tree/delete_tree_file';
import { deleteDir } from '../../services/create_tree/delete_tree_dir';
import { getTreeFile } from '../../services/create_tree/get_tree_file';
import { upload_tree_file } from '../../services/create_tree/upload_tree_file';
import { upload_tree_dir } from '../../services/create_tree/upload_tree_dir';
import { view_tree_file } from '../../services/create_tree/view_tree_file';
import { faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import config from '../../config';
import './ReportTree.css';
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../store/auth/selectors';

const { DirectoryTree } = Tree;

const normalizeString = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();


// Función para truncar el texto a un número máximo de caracteres
const truncateText = (text, maxChars) => {
  return text.length > maxChars ? text.slice(0, maxChars) + '...' : text;
};





const ReportTree = ({ open, onClose, reportData }) => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [, setFilesInProcess] = useState(0);
  const [spinnerVisible, setSpinnerVisible] = useState(false);

  //Control de Permisos
  const appName = 'reportes'; // El nombre de la aplicación actual
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionDelete = permissions.priv_delete === 'Y';
  const permissionUpdate = permissions.priv_update === 'Y';
  const permissionInsert = permissions.priv_insert === 'Y';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const directoryPath = config.baseDirUrl;
        const { data } = await postReports({ directoryPath, codigoProyecto: reportData.nombre_modulo });
        const transformedData = transformData(data, handleDeleteFile, handleDownload, getUploadProps, handleView, getUploadFolderProps);
        setTreeData(transformedData);
        const firstLevelKeys = transformedData.map(node => node.key);
        setExpandedKeys(firstLevelKeys);
      } catch (error) {
        console.error('Error al obtener el árbol de archivos:', error);
      }
    };


    if (open) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportData.nombre_modulo]);

  async function refreshData() {
    setLoading(true);
    try {
      const directoryPath = config.baseDirUrl;
      const { data } = await postReports({ directoryPath, codigoProyecto: reportData.nombre_modulo });
      const transformedData = transformData(data, handleDeleteFile, handleDownload, getUploadProps, handleView, getUploadFolderProps);
      setTreeData(transformedData);
      const firstLevelKeys = transformedData.map(node => node.key);
      setExpandedKeys(firstLevelKeys);
    } catch (error) {
      console.error('Error al obtener el árbol de archivos:', error);
    } finally {
      setLoading(false);
    }
  }
  //Funcion para obtener la extension del archivo
  const getFileExtension = (filePath) => {
    return filePath.split('.').pop().toLowerCase();
  };

  //Funcion para verificar si la extension es visualizable
  const isViewableExtension = (extension) => {
    const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    return viewableExtensions.includes(extension);
  };

  const handleView = async (node) => {
    setSpinnerVisible(true); // Mostrar spinner cuando se inicie la visualización

    const path = node.path;
    if (!path) {
      console.error('No se proporcionó la ruta del archivo');
      message.error('No se proporcionó la ruta del archivo.', 5);
      setSpinnerVisible(false); // Ocultar spinner si no se proporciona la ruta
      return;
    }

    const extension = getFileExtension(path);

    if (!isViewableExtension(extension)) {
      console.error('Tipo de archivo no soportado para visualización');
      message.error('El tipo de archivo no puede ser visualizado en el navegador.', 5);
      setSpinnerVisible(false); // Ocultar spinner si el tipo de archivo no es soportado
      return;
    }

    try {
      const result = await view_tree_file(path);

      if (result.status !== 200) {
        console.error('Error al visualizar el archivo:', result.message);
        message.error('Error al descargar el archivo.', 5);
      } else {
        message.success('Archivo abierto correctamente.');
      }
    } catch (error) {
      console.error('Error al visualizar el archivo:', error.message || error);
      message.error('Error al descargar el archivo.', 5);
    } finally {
      setSpinnerVisible(false); // Ocultar spinner cuando la visualización se complete
    }
  };


  const handleDownload = async (node) => {
    setSpinnerVisible(true);
    try {
      const result = await getTreeFile(node);
      if (result) {
        message.success('Archivo descargado correctamente.');
      } else {
        message.error('Error al descargar el archivo.', 5);
      }
    } catch (error) {
      message.error('Error al descargar el archivo.', 5);
      console.error('Error al descargar el archivo:', error);
    } finally {
      setSpinnerVisible(false); // Ocultar spinner cuando la descarga se complete
    }
  };

  const transformData = (data, handleDeleteFile, handleDownload, getUploadProps, handleView, getUploadFolderProps) => {
    const formatNode = (node) => {
      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Contenedor del texto y el icono */}
            <div style={{ display: 'flex', alignItems: 'center', width: '400px' }}>
              {/* Nombre del archivo o carpeta */}
              <Tooltip title={node.name}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {truncateText(normalizeString(node.name), 55)}
                </span>
              </Tooltip>
            </div>
            {/* Botones de acción */}
            <div style={{ marginLeft: 'auto' }}>
              {node.isLeaf && (
                <>
                  <Tooltip title="Ver">
                    <button
                      className="btn-primary-lis"
                      style={{ background: 'none', border: 'none', color: 'inherit', padding: 0, font: 'inherit', cursor: 'pointer' }}
                      onClick={() => handleView(node)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  </Tooltip>
                  <span style={{ margin: '0 2px', fontSize: '16px' }}> | </span>
                  <Tooltip title="Descargar Archivo">
                    <button
                      className="btn-primary-lis"
                      style={{ background: 'none', border: 'none', color: 'inherit', padding: 0, font: 'inherit', cursor: 'pointer' }}
                      onClick={() => handleDownload(node)}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                  </Tooltip>
                  <span style={{ margin: '0 2px', fontSize: '16px' }}> | </span>
                  {permissionDelete && (
                    <Tooltip title="Eliminar">
                      <Popconfirm
                        title='Eliminación de archivo'
                        description="¿Segur@ que quieres eliminar este archivo?"
                        onConfirm={() => handleDeleteFile(node)}
                        okText="Sí"
                        cancelText="No"
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      >
                        <button
                          className="btn-primary-lis"
                          style={{ background: 'none', border: 'none', color: 'inherit', padding: 0, font: 'inherit', cursor: 'pointer' }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </Popconfirm>
                    </Tooltip>
                  )}
                </>
              )}
              {!node.isLeaf && (
                <>
                  <div className="container" style={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Descargar Carpeta">
                      <button
                        className="btn-primary-lis"
                        style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer' }}
                        onClick={() => handleDownload(node)}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                    </Tooltip>
                    <span style={{ fontSize: '16px', paddingRight: '0px', paddingLeft: '5px' }}> | </span>

                    {permissionInsert && (
                      <>
                        <Tooltip title="Subir Archivo">
                          <Upload {...getUploadProps(node)}>
                            <Button
                              className="btn-primary-lis"
                              style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer' }}
                              icon={<FontAwesomeIcon icon={faCloudUploadAlt} />}
                            />
                          </Upload>
                        </Tooltip>


                        <span style={{ fontSize: '16px', paddingRight: '0px', paddingLeft: '34px' }}> | </span>


                        <Tooltip title="Subir Carpetas">
                          <Upload {...getUploadFolderProps(node)} directory>
                            <Button
                              className="btn-primary-lis"
                              style={{ background: 'none', border: 'none', color: 'inherit', margin: '0 -2px', font: 'inherit', cursor: 'pointer' }}
                              icon={<FontAwesomeIcon icon={faFolderPlus} />}
                            />
                          </Upload>
                        </Tooltip>
                      </>
                    )}


                    <span style={{ fontSize: '16px', paddingRight: '0px', paddingLeft: '27px' }}> | </span>
                    {permissionDelete && (
                      <Tooltip title="Eliminar" >
                        <Popconfirm
                          title='Eliminación de archivo'
                          description="¿Segur@ que quieres eliminar este archivo?"
                          onConfirm={() => handleDeleteFile(node)}
                          okText="Sí"
                          cancelText="No"
                          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        >
                          <button
                            className="btn-primary-lis"
                            style={{ background: 'none', border: 'none', color: 'inherit', margin: '0 4px', font: 'inherit', cursor: 'pointer' }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </Popconfirm>
                      </Tooltip>
                    )}

                  </div>
                </>
              )}
            </div>
          </div>
        ),
        key: node.key,
        isLeaf: node.isLeaf,
        pathFromClient: node.pathFromClient || node.path, // Asigna pathFromClient si está presente, sino usa path
        children: node.children ? node.children.map(formatNode) : [],
      };
    };

    return data.map(formatNode);
  };

  const getUploadProps = (node) => ({
    name: 'file',
    multiple: true,
    showUploadList: false,
    customRequest: async (info) => {
      setSpinnerVisible(true); // Mostrar spinner cuando se inicie la carga
      const { file } = info;
      // Verificar el tamaño del archivo
      const maxSize = 100 * 1024 * 1024; // 300 MB en bytes
      if (file.size > maxSize) {
        message.error(`El archivo "${file.name}" es demasiado pesado para cargar. El límite es 100 MB.`, 5);
        setSpinnerVisible(false); // Ocultar spinner si el archivo es demasiado pesado
        return;
      }

      if (!node.path) {
        message.error('Debe seleccionar un elemento del árbol para cargar archivos.', 5);
        setSpinnerVisible(false); // Ocultar spinner si no hay una ruta válida
        return;
      }

      try {
        const uploadedBy = 'defaultUser'; // Puedes obtener esto de otro lugar si es necesario
        const response = await upload_tree_file(node.path, file, uploadedBy);

        if (response.status === 'success') {
          file.serverFileName = response.serverFileName;
          refreshData();
          message.success(`Archivo cargado correctamente: ${file.name}`);
        } else {
          message.error(`Error en la carga: ${file.name}`, 5);
        }
      } catch (error) {
        message.error(`Error al cargar el archivo:: ${error.message}`, 5);
      } finally {
        setSpinnerVisible(false); // Ocultar spinner cuando la carga se complete
      }
    },
    onClick: (e) => {
      e.stopPropagation();
    },
  });





  const handleDeleteFile = async (node) => {
    setSpinnerVisible(true); // Mostrar spinner cuando se inicie la eliminación

    const basePath = 'DocumentosFAO/DocumentosReportes/';
    const fullPath = node.path ||
      (node.pathFromClient && node.serverFileName ?
        `${node.pathFromClient}/${node.serverFileName}` :
        null);

    if (!fullPath) {
      message.error('No se encontró el nombre del archivo en el servidor.', 5);
      setSpinnerVisible(false); // Ocultar spinner si no se encuentra el archivo
      return;
    }

    const filePath = `${basePath}${fullPath}`;
    try {
      let result;
      if (node.isLeaf) {
        // Eliminar archivo
        result = await deleteFile(filePath);
      } else {
        // Eliminar carpeta
        result = await deleteDir(filePath);
      }

      if (result) {
        message.success(node.isLeaf ? 'Archivo eliminado correctamente.' : 'Carpeta eliminada correctamente.');
        // Actualiza el árbol después de eliminar el archivo o carpeta
        refreshData();
      } else {
        message.error(node.isLeaf ? 'Error al eliminar el archivo.' : 'Error al eliminar la carpeta.', 5);
      }
    } catch (error) {
      message.error(node.isLeaf ? 'Error al eliminar el archivo.' : 'Error al eliminar la carpeta.', 5);
      console.error('Error al eliminar:', error);
    } finally {
      setSpinnerVisible(false); // Ocultar spinner cuando la eliminación se complete
    }
  };




  const download_folder = async (nodo) => {
    setSpinnerVisible(true); // Mostrar spinner cuando se inicie la descarga
    try {
      const result = await getTreeFile(nodo);
      if (result) {
        message.success('Archivo descargado correctamente.');
      } else {
        message.error('Error al descargar el archivo.', 5);
      }
    } catch (error) {
      message.error('Error al descargar el archivo.', 5);
      console.error('Error al descargar el archivo:', error);
    } finally {
      setSpinnerVisible(false); // Ocultar spinner cuando la descarga se complete
    }
  };


  const getUploadFolderProps = (node) => ({
    name: 'file',
    multiple: true,
    showUploadList: false,
    customRequest: async (info) => {
      setFilesInProcess(prev => {
        const newFilesInProcess = prev + 1;
        if (newFilesInProcess === 1) {
          setSpinnerVisible(true); // Mostrar spinner solo cuando se comienza la carga
        }
        return newFilesInProcess; // Incrementar el contador de archivos en proceso
      });
      const { file } = info;
      // Verificar el tamaño del archivo o carpeta
      const maxSize = 100 * 1024 * 1024; // 300 MB en bytes
      if (file.size > maxSize) {
        message.error(`El archivo "${file.name}" es demasiado pesado para cargar. El límite es 100 MB.`, 5);
        setFilesInProcess(prev => {
          const newFilesInProcess = prev - 1;
          if (newFilesInProcess === 0) {
            setSpinnerVisible(false); // Ocultar spinner si no hay archivos en proceso
          }
          return newFilesInProcess; // Decrementar el contador si el archivo es demasiado pesado
        });
        return;
      }

      let varpath = '';
      if (!node.path) {
        if (!node.nombre_modulo) {
          message.error('Debe seleccionar un elemento del árbol para cargar archivos.', 5);
          setFilesInProcess(prev => {
            const newFilesInProcess = prev - 1;
            if (newFilesInProcess === 0) {
              setSpinnerVisible(false); // Ocultar spinner si no hay archivos en proceso
            }
            return newFilesInProcess; // Decrementar el contador si no se puede cargar
          });
          return;
        } else {
          varpath = node.nombre_modulo; // Asignar cadena vacía si node.nombre_modulo tiene un valor
        }
      } else {
        varpath = node.path; // Asignar node.path si no está vacío
      }

      try {
        // Crear un objeto con la información del archivo
        const fileData = {
          file: file,
          webkitRelativePath: file.webkitRelativePath || '', // Si el webkitRelativePath está disponible
        };
        const response = await upload_tree_dir(varpath, [fileData]);

        if (response.status === 'success') {
          file.serverFileName = response.serverFileName;
        } else {
          message.error(`Error en la carga: ${file.name}`, 5);

        }
      } catch (error) {
        console.error('Error al cargar la carpeta:', error);
        message.error(`Error al cargar la carpeta: ${error.message}`, 5);
      } finally {
        setFilesInProcess(prev => {
          const newFilesInProcess = prev - 1;
          if (newFilesInProcess === 0) {
            message.success('Carga de archivos completada.');
            setSpinnerVisible(false); // Ocultar spinner si todos los archivos han sido procesados
            refreshData();
          }
          return newFilesInProcess;
        });
      }
    },
    onClick: (e) => {
      e.stopPropagation();
    },
  });







  return (
    <Drawer
      title={`Verificar ${reportData.nombre_modulo}`}
      placement="right"
      size="large"
      onClose={onClose}
      open={open}
      extra={

        <Space>
          {permissionInsert && (

            <Tooltip title="Subir Carpetas">
              <Upload style={{ paddingLeft: '30px' }}
                {...getUploadFolderProps(reportData)} directory>
                <div style={{ paddingLeft: '135px' }}>
                  <Button
                    style={{ background: '#fff', color: '#e5ac1a' }}
                    icon={<FolderOpenOutlined />}
                    loading={loading}
                  >
                    Importar
                  </Button></div>
              </Upload>
            </Tooltip>
          )}

          <div style={{ paddingRight: '115px' }}>
            <Tooltip title="Descargar Carpeta">
              <Button
                type="primary"
                style={{ marginRight: '5px' }}
                icon={<CloudUploadOutlined />}
                onClick={() => download_folder({ path: reportData.nombre_modulo, name: reportData.nombre_modulo })}
                loading={loading}
              >
                Descargar
              </Button>
            </Tooltip></div>
          <Tooltip title="Cerrar">
            <Button
              danger
              onClick={onClose}
              icon={<CloseOutlined />}
            >
              Cancelar
            </Button></Tooltip>
        </Space>
      }
    >
      {spinnerVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Spin size="large" style={{ paddingLeft: '20px' }}
          />
        </div>
      )}

      <h6><strong>Estado:</strong> {reportData.estado}</h6>
      <h6><strong>Periodo:</strong> {reportData.periodo}</h6>
      <DirectoryTree
        multiple
        defaultExpandAll
        expandedKeys={expandedKeys}
        onExpand={(keys) => setExpandedKeys(keys)}
        treeData={treeData}
      />
    </Drawer>
  );
};

export default ReportTree;